import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Button,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialog: {
      '& .MuiDialog-paper': {
        maxWidth: '1200px',
        width: '100%',
        maxHeight: '90vh',
      },
    },
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: '24px 24px 16px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    titleContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    title: {
      fontSize: '20px',
      fontWeight: 600,
      color: theme.palette.text.primary,
      margin: 0,
    },
    breadcrumb: {
      fontSize: '14px',
      color: theme.palette.text.secondary,
    },
    closeButton: {
      padding: '8px',
      marginTop: '-8px',
    },
    dialogContent: {
      padding: '24px',
    },
    filtersContainer: {
      display: 'flex',
      gap: '16px',
      marginBottom: '16px',
      flexWrap: 'wrap',
    },
    resultsCount: {
      fontSize: '14px',
      color: theme.palette.text.secondary,
      marginBottom: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    clearButton: {
      textTransform: 'none',
    },
    searchField: {
      flex: '1 1 300px',
      minWidth: '200px',
    },
    filterField: {
      flex: '1 1 200px',
      minWidth: '150px',
    },
    tableContainer: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '8px',
      maxHeight: '500px',
    },
    table: {
      minWidth: 650,
    },
    tableHeader: {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F5F5F5',
    },
    tableHeaderCell: {
      fontWeight: 600,
      fontSize: '14px',
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: '16px',
    },
    tableCell: {
      fontSize: '14px',
      color: theme.palette.text.primary,
      padding: '16px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    promptCell: {
      maxWidth: '300px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    tableRow: {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  }),
);

export interface SimilarPrompt {
  id: string;
  prompt: string;
  domain: string;
  subDomain: string;
  persona: string;
  createdBy: string;
  createdDate: string;
}

export interface SimilarPromptsModalProps {
  open: boolean;
  onClose: () => void;
  prompts: SimilarPrompt[];
  title?: string;
  breadcrumb?: string;
}

type SortField = 'prompt' | 'domain' | 'subDomain' | 'persona' | 'createdBy' | 'createdDate';
type SortDirection = 'asc' | 'desc';

export const SimilarPromptsModal: React.FC<SimilarPromptsModalProps> = ({
  open,
  onClose,
  prompts,
  title = 'Similar Prompts',
  breadcrumb = 'Data Dashboards > Similar Prompts',
}) => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [subDomainFilter, setSubDomainFilter] = useState('');
  const [personaFilter, setPersonaFilter] = useState('');
  const [sortField, setSortField] = useState<SortField>('createdDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Get unique values for filters
  const domains = Array.from(new Set(prompts.map(p => p.domain))).filter(Boolean);
  const subDomains = Array.from(new Set(prompts.map(p => p.subDomain))).filter(Boolean);
  const personas = Array.from(new Set(prompts.map(p => p.persona))).filter(Boolean);

  // Filter prompts
  const filteredPrompts = prompts.filter(prompt => {
    const matchesSearch = !searchQuery || 
      prompt.prompt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prompt.createdBy.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDomain = !domainFilter || prompt.domain === domainFilter;
    const matchesSubDomain = !subDomainFilter || prompt.subDomain === subDomainFilter;
    const matchesPersona = !personaFilter || prompt.persona === personaFilter;
    
    // Date filter - match by date string
    let matchesDate = true;
    if (dateFilter) {
      // Convert filter date to MM/DD/YYYY format for comparison
      const filterDate = new Date(dateFilter);
      const filterDateStr = `${String(filterDate.getMonth() + 1).padStart(2, '0')}/${String(filterDate.getDate()).padStart(2, '0')}/${filterDate.getFullYear()}`;
      matchesDate = prompt.createdDate === filterDateStr;
    }
    
    return matchesSearch && matchesDomain && matchesSubDomain && matchesPersona && matchesDate;
  });

  // Sort prompts
  const sortedPrompts = [...filteredPrompts].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setDateFilter('');
    setDomainFilter('');
    setSubDomainFilter('');
    setPersonaFilter('');
  };

  const hasActiveFilters = searchQuery || dateFilter || domainFilter || subDomainFilter || personaFilter;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth={false}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box className={classes.titleContent}>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.breadcrumb}>{breadcrumb}</Typography>
        </Box>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        {/* Filters Section */}
        <Box className={classes.filtersContainer}>
          <TextField
            className={classes.searchField}
            placeholder="Search prompts or users"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            className={classes.filterField}
            label="Filter by Created Date"
            placeholder="Select date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            variant="outlined"
            size="small"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
          />
          
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Filter by Domain</InputLabel>
            <Select
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value as string)}
              label="Filter by Domain"
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {domains.map((domain) => (
                <MenuItem key={domain} value={domain}>
                  {domain}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Filter by Sub-domain</InputLabel>
            <Select
              value={subDomainFilter}
              onChange={(e) => setSubDomainFilter(e.target.value as string)}
              label="Filter by Sub-domain"
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {subDomains.map((subDomain) => (
                <MenuItem key={subDomain} value={subDomain}>
                  {subDomain}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Filter by persona</InputLabel>
            <Select
              value={personaFilter}
              onChange={(e) => setPersonaFilter(e.target.value as string)}
              label="Filter by persona"
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {personas.map((persona) => (
                <MenuItem key={persona} value={persona}>
                  {persona}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Results Count and Clear Filters */}
        <Box className={classes.resultsCount}>
          <Typography>
            Showing {sortedPrompts.length} of {prompts.length} prompts
          </Typography>
          {hasActiveFilters && (
            <Button
              className={classes.clearButton}
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              color="primary"
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Table Section */}
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} stickyHeader>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell className={classes.tableHeaderCell}>
                  <TableSortLabel
                    active={sortField === 'prompt'}
                    direction={sortField === 'prompt' ? sortDirection : 'asc'}
                    onClick={() => handleSort('prompt')}
                  >
                    Prompts
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  <TableSortLabel
                    active={sortField === 'domain'}
                    direction={sortField === 'domain' ? sortDirection : 'asc'}
                    onClick={() => handleSort('domain')}
                  >
                    Domain
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  <TableSortLabel
                    active={sortField === 'subDomain'}
                    direction={sortField === 'subDomain' ? sortDirection : 'asc'}
                    onClick={() => handleSort('subDomain')}
                  >
                    Sub Domain
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  <TableSortLabel
                    active={sortField === 'persona'}
                    direction={sortField === 'persona' ? sortDirection : 'asc'}
                    onClick={() => handleSort('persona')}
                  >
                    Persona
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  <TableSortLabel
                    active={sortField === 'createdBy'}
                    direction={sortField === 'createdBy' ? sortDirection : 'asc'}
                    onClick={() => handleSort('createdBy')}
                  >
                    Created by
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  <TableSortLabel
                    active={sortField === 'createdDate'}
                    direction={sortField === 'createdDate' ? sortDirection : 'asc'}
                    onClick={() => handleSort('createdDate')}
                  >
                    Created date
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPrompts.length > 0 ? (
                sortedPrompts.map((prompt) => (
                  <TableRow key={prompt.id} className={classes.tableRow}>
                    <TableCell className={`${classes.tableCell} ${classes.promptCell}`}>
                      <Tooltip title={prompt.prompt} placement="top">
                        <span>{prompt.prompt}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.tableCell}>{prompt.domain}</TableCell>
                    <TableCell className={classes.tableCell}>{prompt.subDomain}</TableCell>
                    <TableCell className={classes.tableCell}>{prompt.persona}</TableCell>
                    <TableCell className={classes.tableCell}>{prompt.createdBy}</TableCell>
                    <TableCell className={classes.tableCell}>{prompt.createdDate}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" className={classes.tableCell}>
                    No prompts found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};
